import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Search, Send, MessageSquareText, Phone, Loader2, ArrowLeft, Ban, Check, CheckCheck, Clock as ClockIcon, AlertCircle, Trash2, Pencil, X as XIcon, Save, Moon, BellOff, Star, Share2 } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";
import AttachToolbar, { AttachmentPreview } from "@/components/AttachToolbar";
import SegmentMeter from "@/components/SegmentMeter";
import ComposeModal from "@/components/ComposeModal";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function dayKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (dayKey(iso) === dayKey(today.toISOString())) return "Today";
  if (dayKey(iso) === dayKey(yest.toISOString())) return "Yesterday";
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString([], sameYear ? { weekday: "short", month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

function fullDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function Inbox() {
  const { conversationId } = useParams();
  const nav = useNavigate();
  // Hydrate Inbox from sessionStorage so switching to it feels instant.
  const cachedConvs = (() => { try { return JSON.parse(sessionStorage.getItem("sts:convs") || "null") || []; } catch { return []; } })();
  const [convs, setConvs] = useState(cachedConvs);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all"); // all | unread | followup
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [shortenLinks, setShortenLinks] = useState(true);
  const [msgHits, setMsgHits] = useState([]);
  const [convHits, setConvHits] = useState([]);
  const [searchingMsgs, setSearchingMsgs] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editFirst, setEditFirst] = useState("");
  const [editLast, setEditLast] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardBody, setForwardBody] = useState("");
  const [forwardMedia, setForwardMedia] = useState([]);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data);
      try { sessionStorage.setItem("sts:convs", JSON.stringify(data)); } catch {}
      // Only auto-open the first conversation on desktop; on mobile show the list first
      const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
      if (!conversationId && data.length && !active && isDesktop) {
        nav(`/inbox/${data[0].id}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { loadConvs(); }, []);

  // Search message bodies across all conversations when user types in search box
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setMsgHits([]); setConvHits([]); return; }
    setSearchingMsgs(true);
    const handle = setTimeout(async () => {
      try {
        const [m, c] = await Promise.all([
          api.get("/messages/search", { params: { q } }),
          api.get("/conversations-search", { params: { q } }),
        ]);
        setMsgHits(m.data || []);
        setConvHits(c.data || []);
      } catch (_) { setMsgHits([]); setConvHits([]); }
      finally { setSearchingMsgs(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!conversationId) return;
    const inList = convs.find((x) => x.id === conversationId);
    if (inList) setActive(inList);
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${conversationId}/messages`);
        setMessages(data);
        // If this conversation isn't in the sidebar list (e.g. hidden after a broadcast
        // with no reply), fetch it directly so the header + composer show up.
        if (!inList) {
          try {
            const { data: convDoc } = await api.get(`/conversations/${conversationId}`);
            setActive(convDoc);
          } catch { /* if the fetch fails, header just stays blank */ }
        }
        // Persist the read state on the server so the Unread tab drops this convo
        api.post(`/conversations/${conversationId}/read`).catch(() => {});
        setConvs((prev) => prev.map((cv) => cv.id === conversationId ? { ...cv, unread_count: 0 } : cv));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [conversationId, convs.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if ((!body.trim() && attachments.length === 0) || !active) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages/send", {
        contact_id: active.contact_id,
        body,
        media_urls: attachments.map((a) => a.url),
        shorten_links: shortenLinks,
      });
      setMessages((prev) => [...prev, data.message]);
      setActive((a) => a ? { ...a, awaiting_reply: false } : a);
      setBody("");
      setAttachments([]);
      loadConvs();
      toast.success("Message sent");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const markReplied = async () => {
    if (!active) return;
    try {
      await api.post(`/conversations/${active.id}/mark-replied`);
      setConvs((prev) => prev.map((c) => c.id === active.id ? { ...c, awaiting_reply: false } : c));
      setActive((a) => a ? { ...a, awaiting_reply: false } : a);
      toast.success("Marked as replied");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) { setBody((b) => b + emoji); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const nowIso = new Date().toISOString();
  const isSnoozed = (c) => !!(c.snoozed_until && c.snoozed_until > nowIso);
  const snoozedUntilLabel = (c) => {
    if (!c.snoozed_until) return "";
    const t = new Date(c.snoozed_until);
    return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };
  const isUnreadish = (c) => ((c.unread_count || 0) > 0 || !!c.awaiting_reply) && !isSnoozed(c);
  const unreadCount = convs.filter(isUnreadish).length;
  const followupCount = convs.filter((c) => c.needs_reply).length;

  const snoozeActive = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(`/conversations/${active.id}/snooze`);
      setConvs((prev) => prev.map((c) => c.id === active.id ? { ...c, snoozed_until: data.snoozed_until, unread_count: 0 } : c));
      setActive((a) => a ? { ...a, snoozed_until: data.snoozed_until } : a);
      toast.success("Snoozed until tomorrow morning");
      nav("/inbox");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unsnoozeActive = async () => {
    if (!active) return;
    try {
      await api.post(`/conversations/${active.id}/unsnooze`);
      setConvs((prev) => prev.map((c) => c.id === active.id ? { ...c, snoozed_until: null } : c));
      setActive((a) => a ? { ...a, snoozed_until: null } : a);
      toast.success("Snooze cleared");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleFollowup = async () => {
    if (!active) return;
    const wasFlagged = !!active.needs_reply;
    try {
      const url = wasFlagged ? "unflag" : "flag";
      await api.post(`/conversations/${active.id}/${url}`);
      setConvs((prev) => prev.map((c) => c.id === active.id ? { ...c, needs_reply: !wasFlagged } : c));
      setActive((a) => a ? { ...a, needs_reply: !wasFlagged } : a);
      toast.success(wasFlagged ? "Removed from Follow-up" : "Added to Follow-up");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const searchPool = search.trim().length >= 2
    ? [...convs, ...convHits.filter((h) => !convs.some((c) => c.id === h.id))]
    : convs;
  const filtered = searchPool.filter((c) => {
    const q = search.toLowerCase();
    const matchesSearch = !search ||
      (c.contact_name || "").toLowerCase().includes(q) ||
      (c.contact_phone || "").includes(search) ||
      (c.student_names || []).some((s) => (s || "").toLowerCase().includes(q)) ||
      convHits.some((h) => h.id === c.id);
    let matchesTab = true;
    if (tab === "unread") matchesTab = isUnreadish(c);
    else if (tab === "followup") matchesTab = !!c.needs_reply;
    return matchesSearch && matchesTab;
  });

  return (
    <div className="flex h-full w-full min-w-0" data-testid="inbox-page">
      {/* Conversation List */}
      <div className={`${conversationId ? "hidden md:flex" : "flex"} w-full md:w-[340px] border-r border-slate-200 bg-white flex-col shrink-0`}>
        <div className="h-16 px-4 border-b border-slate-200 flex items-center gap-2">
          <h2 className="text-lg font-bold text-slate-900 flex-1">Inbox</h2>
          <span className="text-xs text-slate-500">{convs.length} total</span>
        </div>
        <div className="px-3 pt-3 pb-2 flex gap-1.5 border-b border-slate-100">
          <button
            onClick={() => setTab("all")}
            data-testid="inbox-tab-all"
            className={`flex-1 h-8 px-2 rounded-md text-xs font-semibold transition-colors ${tab === "all" ? "bg-[#D9232E] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            All <span className={`ml-1 ${tab === "all" ? "text-white/80" : "text-slate-400"}`}>{convs.length}</span>
          </button>
          <button
            onClick={() => setTab("unread")}
            data-testid="inbox-tab-unread"
            className={`flex-1 h-8 px-2 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${tab === "unread" ? "bg-[#D9232E] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            Unread
            <span className={`min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${tab === "unread" ? "bg-white text-[#D9232E]" : (unreadCount > 0 ? "bg-[#D9232E] text-white" : "bg-white text-slate-500")}`}>
              {unreadCount}
            </span>
          </button>
          <button
            onClick={() => setTab("followup")}
            data-testid="inbox-tab-followup"
            className={`flex-1 h-8 px-2 rounded-md text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${tab === "followup" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            title="Conversations you flagged as needing a reply"
          >
            <Star className={`w-3 h-3 ${tab === "followup" ? "fill-white" : (followupCount > 0 ? "fill-amber-500 text-amber-500" : "")}`} />
            Follow-up
            {followupCount > 0 && (
              <span className={`min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${tab === "followup" ? "bg-white text-amber-600" : "bg-amber-500 text-white"}`}>
                {followupCount}
              </span>
            )}
          </button>
        </div>
        <div className="p-3 border-b border-slate-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-slate-50 border-slate-200"
              data-testid="inbox-search-input"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll" data-testid="conversation-list">
          {/* Message search results (only when searching 2+ chars) */}
          {search.trim().length >= 2 && (
            <div className="border-b border-slate-100 bg-slate-50/50">
              <div className="px-4 pt-3 pb-1 flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Messages matching "{search}"</div>
                <div className="text-[10px] text-slate-400">{searchingMsgs ? "searching..." : `${msgHits.length} hit${msgHits.length === 1 ? "" : "s"}`}</div>
              </div>
              {msgHits.length === 0 && !searchingMsgs && (
                <div className="px-4 pb-3 text-xs text-slate-400 italic">No messages match.</div>
              )}
              <div className="max-h-80 overflow-y-auto thin-scroll" data-testid="msg-search-hits">
              {msgHits.map((m) => (
                <button
                  key={m.id}
                  onClick={() => nav(`/inbox/${m.conversation_id}`)}
                  data-testid={`msg-search-hit-${m.id}`}
                  className="w-full text-left px-4 py-2.5 border-b border-slate-100 hover:bg-red-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-900 truncate">{m.contact_name || m.contact_phone || "Unknown"}</div>
                    <div className="text-[10px] text-slate-400 shrink-0" title={fullDateTime(m.created_at)}>{timeAgo(m.created_at)}</div>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 line-clamp-2 leading-snug">
                    <span className="text-[10px] text-slate-400 mr-1 uppercase">{m.direction === "out" ? "sent" : "recv"}</span>
                    {m.body}
                  </div>
                </button>
              ))}
              </div>
              <div className="px-4 py-1.5 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50/50">Conversations</div>
            </div>
          )}
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">
              {tab === "unread" ? "You're all caught up 🎉" : tab === "followup" ? "No conversations flagged for follow-up. Star any thread to add it here." : "No conversations"}
            </div>
          )}
          {filtered.map((c) => {
            const isActive = c.id === conversationId;
            return (
              <button
                key={c.id}
                onClick={() => nav(`/inbox/${c.id}`)}
                data-testid={`conversation-item-${c.id}`}
                className={`w-full text-left px-4 py-3.5 border-b border-slate-100 transition-colors ${
                  isActive ? "bg-red-50 border-l-4 border-l-[#D9232E]" : "hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0">
                      {(c.contact_name || "??").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                        {c.needs_reply && (
                          <Star
                            className="w-3.5 h-3.5 fill-amber-500 text-amber-500 shrink-0"
                            data-testid={`followup-badge-${c.id}`}
                            aria-label="Needs reply"
                          />
                        )}
                        <span className="truncate">{c.contact_name}</span>
                        {c.opted_out && (
                          <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-800 text-white uppercase tracking-wide" data-testid={`opted-out-badge-${c.id}`}>STOP</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || c.contact_phone}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-[11px] text-slate-400" title={fullDateTime(c.last_message_at)}>{timeAgo(c.last_message_at)}</span>
                    {isSnoozed(c) ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full bg-slate-800 text-white text-[9px] font-bold uppercase tracking-wide"
                        data-testid={`snoozed-badge-${c.id}`}
                        title={`Snoozed until ${snoozedUntilLabel(c)}`}
                      >
                        <Moon className="w-2.5 h-2.5" /> Snoozed
                      </span>
                    ) : c.unread_count > 0 ? (
                      <span className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-[#D9232E] text-white text-[10px] font-bold flex items-center justify-center">
                        {c.unread_count}
                      </span>
                    ) : c.awaiting_reply ? (
                      <span className="px-1.5 h-[18px] rounded-full border border-[#D9232E] text-[#D9232E] text-[9px] font-bold uppercase tracking-wide flex items-center" data-testid={`awaiting-reply-badge-${c.id}`} title="Parent texted and hasn't had a 1-on-1 reply yet">
                        Reply
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Thread View */}
      <div className={`${conversationId ? "flex" : "hidden md:flex"} flex-1 flex-col bg-[#FAFBFC] min-w-0`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center flex-col gap-3 text-slate-400" data-testid="empty-thread">
            <MessageSquareText className="w-12 h-12" strokeWidth={1.5} />
            <p className="text-sm">Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            <div className="h-16 px-4 md:px-6 border-b border-slate-200 bg-white flex items-center gap-3">
              <button
                type="button"
                onClick={() => nav("/inbox")}
                className="md:hidden -ml-1 p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                data-testid="thread-back-button"
                aria-label="Back to conversations"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-700 shrink-0">
                {(active.contact_name || "??").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              {editingName ? (
                <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
                  <input
                    value={editFirst}
                    onChange={(e) => setEditFirst(e.target.value)}
                    placeholder="First name"
                    className="text-sm font-bold text-slate-900 bg-white border border-slate-300 rounded px-2 py-1 w-32 focus:outline-none focus:border-[#D9232E]"
                    data-testid="thread-edit-first"
                    autoFocus
                  />
                  <input
                    value={editLast}
                    onChange={(e) => setEditLast(e.target.value)}
                    placeholder="Last name"
                    className="text-sm font-bold text-slate-900 bg-white border border-slate-300 rounded px-2 py-1 w-32 focus:outline-none focus:border-[#D9232E]"
                    data-testid="thread-edit-last"
                  />
                  <input
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="Phone"
                    className="text-xs text-slate-700 bg-white border border-slate-300 rounded px-2 py-1 w-32 focus:outline-none focus:border-[#D9232E]"
                    data-testid="thread-edit-phone"
                  />
                  <button
                    type="button"
                    disabled={savingName}
                    onClick={async () => {
                      setSavingName(true);
                      try {
                        const { data: updated } = await api.put(`/contacts/${active.contact_id}`, {
                          first_name: editFirst.trim(),
                          last_name: editLast.trim(),
                          phone: editPhone.trim() || undefined,
                        });
                        // Use the server's authoritative display name (joined student names)
                        // so the Inbox reflects what the backend actually stored.
                        const newDisplay = updated?.display_name || `${editFirst.trim()} ${editLast.trim()}`.trim() || active.contact_phone;
                        const newPhone = updated?.phone || editPhone.trim() || active.contact_phone;
                        setActive((a) => a ? { ...a, contact_name: newDisplay, contact_phone: newPhone } : a);
                        setConvs((prev) => prev.map((c) => c.contact_id === active.contact_id ? { ...c, contact_name: newDisplay, contact_phone: newPhone } : c));
                        setEditingName(false);
                        toast.success("Contact updated");
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setSavingName(false); }
                    }}
                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                    data-testid="thread-edit-save"
                    title="Save"
                  >
                    {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(false)}
                    className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                    data-testid="thread-edit-cancel"
                    title="Cancel"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  className="flex-1 min-w-0 group cursor-pointer"
                  onClick={() => {
                    const parts = (active.contact_name || "").trim().split(/\s+/);
                    setEditFirst(parts[0] || "");
                    setEditLast(parts.slice(1).join(" ") || "");
                    setEditPhone(active.contact_phone || "");
                    setEditingName(true);
                  }}
                  data-testid="thread-name-clickable"
                  title="Click to edit"
                >
                  <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-2" data-testid="active-contact-name">
                    <span className="truncate">{active.contact_name}</span>
                    <Pencil className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    {active.opted_out && (
                      <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-800 text-white uppercase tracking-wide">Opted out</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                    <Phone className="w-3 h-3 shrink-0" /> {active.contact_phone}
                  </div>
                </div>
              )}
              {active.awaiting_reply && (
                <button
                  type="button"
                  onClick={markReplied}
                  className="shrink-0 h-8 px-2.5 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors flex items-center gap-1"
                  data-testid="thread-mark-replied-button"
                  title="Answered another way (call, in person). Removes it from Unread."
                >
                  <CheckCheck className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Mark replied</span>
                </button>
              )}
              <button
                type="button"
                onClick={toggleFollowup}
                className={`shrink-0 p-2 rounded-md transition-colors ${active.needs_reply ? "text-amber-500 hover:bg-amber-50" : "text-slate-400 hover:text-amber-500 hover:bg-amber-50"}`}
                data-testid="thread-followup-button"
                title={active.needs_reply ? "Remove from Follow-up" : "Flag for follow-up — reappears in the Follow-up tab until you reply"}
                aria-label="Toggle follow-up flag"
              >
                <Star className={`w-4 h-4 ${active.needs_reply ? "fill-amber-500" : ""}`} />
              </button>
              {isSnoozed(active) ? (
                <button
                  type="button"
                  onClick={unsnoozeActive}
                  className="shrink-0 p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors flex items-center gap-1.5"
                  data-testid="thread-unsnooze-button"
                  title={`Snoozed until ${snoozedUntilLabel(active)} — click to wake now`}
                >
                  <BellOff className="w-4 h-4" />
                  <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wide">Snoozed · {snoozedUntilLabel(active)}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={snoozeActive}
                  className="shrink-0 p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                  data-testid="thread-snooze-button"
                  title="Snooze this conversation until 8 AM tomorrow"
                  aria-label="Snooze conversation"
                >
                  <Moon className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Delete this entire thread with ${active.contact_name}? All messages will be permanently removed. The contact itself will NOT be deleted.`)) return;
                  try {
                    await api.delete(`/conversations/${active.id}`);
                    toast.success("Thread deleted");
                    setConvs((prev) => prev.filter((c) => c.id !== active.id));
                    setActive(null);
                    setMessages([]);
                    nav("/inbox");
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className="shrink-0 p-2 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded-md transition-colors"
                data-testid="thread-delete-button"
                aria-label="Delete conversation"
                title="Delete this conversation"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {active.opted_out && (
              <div className="mx-4 md:mx-6 mt-4 px-4 py-3 bg-slate-900 text-white rounded-lg flex items-start gap-3" data-testid="opted-out-banner">
                <Ban className="w-4 h-4 mt-0.5 shrink-0 text-red-400" />
                <div className="text-xs leading-relaxed flex-1">
                  <div className="font-semibold text-sm mb-0.5">This contact opted out</div>
                  <div className="text-slate-300">They replied STOP or were manually opted out. Outbound texts are blocked. They'll be opted back in automatically if they text START — or you can restore them from the <span className="underline">Opted Out</span> tab.</div>
                </div>
              </div>
            )}

            <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-4 md:px-6 py-4 md:py-6 space-y-3" data-testid="message-list">
              {messages.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-10">No messages yet. Send the first one below.</div>
              )}
              {messages.map((m, idx) => {
                const s = (m.status || "").toLowerCase();
                const failed = ["failed", "undelivered"].includes(s);
                const delivered = s === "delivered";
                const read = s === "read";
                const sent = ["sent", "sent_stub"].includes(s);
                const queued = ["queued", "accepted", "sending", "scheduled"].includes(s);
                let statusIcon = null;
                let statusLabel = "";
                let statusClass = m.direction === "out" ? "text-white/70" : "text-slate-400";
                if (m.direction === "out") {
                  if (failed) { statusIcon = <AlertCircle className="w-3 h-3" />; statusLabel = m.error_code ? `failed (${m.error_code})` : "failed"; statusClass = "text-red-100"; }
                  else if (read) { statusIcon = <CheckCheck className="w-3 h-3" />; statusLabel = "read"; statusClass = "text-emerald-200"; }
                  else if (delivered) { statusIcon = <CheckCheck className="w-3 h-3" />; statusLabel = "delivered"; statusClass = "text-white/85"; }
                  else if (sent) { statusIcon = <Check className="w-3 h-3" />; statusLabel = "sent"; }
                  else if (queued) { statusIcon = <ClockIcon className="w-3 h-3" />; statusLabel = "queued"; }
                  else if (s) { statusLabel = s; }
                }
                const prev = idx > 0 ? messages[idx - 1] : null;
                const showDay = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
                return (
                <div key={m.id}>
                  {showDay && (
                    <div className="flex items-center justify-center my-3" data-testid={`day-divider-${dayKey(m.created_at)}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                        {dayLabel(m.created_at)}
                      </span>
                    </div>
                  )}
                  <div className={`group/msg flex ${m.direction === "out" ? "justify-end" : "justify-start"} items-center gap-2`}>
                  {m.direction === "out" && (
                    <button
                      type="button"
                      onClick={() => { setForwardBody(m.body || ""); setForwardMedia(m.media_urls || []); setForwardOpen(true); }}
                      className="opacity-0 group-hover/msg:opacity-100 transition-opacity p-1.5 rounded-full text-slate-400 hover:text-[#D9232E] hover:bg-red-50"
                      data-testid={`forward-message-${m.id}`}
                      title="Forward this message"
                      aria-label="Forward this message"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <div className={`max-w-[85%] md:max-w-[70%] px-4 py-2.5 text-sm leading-relaxed break-words ${m.direction === "out" ? "bubble-out" : "bubble-in"} ${failed ? "opacity-90 ring-2 ring-red-400" : ""}`}>
                    {m.media_urls && m.media_urls.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-1.5">
                        {m.media_urls.map((u, i) => (
                          <a key={i} href={u} target="_blank" rel="noreferrer">
                            <img src={u} alt="attachment" className="max-w-[220px] max-h-[220px] rounded-md object-cover border border-white/20" />
                          </a>
                        ))}
                      </div>
                    )}
                    {m.body && <div>{m.body}</div>}
                    <div className={`text-[10px] mt-1 flex items-center gap-1 ${statusClass}`} title={fullDateTime(m.created_at)}>
                      <span>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      {m.direction === "out" && m.sent_by_name && (
                        <span className="ml-1 flex items-center gap-1" data-testid={`message-sender-${m.id}`}>
                          <span>·</span>
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${m.sent_by_name === "Jackrabbit" ? "bg-amber-400 text-slate-900" : "bg-white/15"}`}
                            title={m.sent_by_name === "Jackrabbit" ? "Sent from Jackrabbit" : m.sent_by_name}
                            data-testid={m.sent_by_name === "Jackrabbit" ? `jr-badge-${m.id}` : undefined}
                          >
                            {m.sent_by_initials || (m.sent_by_name || "").slice(0, 2).toUpperCase()}
                          </span>
                        </span>
                      )}
                      {m.direction === "out" && statusLabel && (
                        <span className="ml-1 flex items-center gap-0.5" data-testid={`message-status-${m.id}`}>
                          <span>·</span>
                          {statusIcon}
                          <span>{statusLabel}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {m.direction === "in" && (
                    <button
                      type="button"
                      onClick={() => { setForwardBody(m.body || ""); setForwardMedia(m.media_urls || []); setForwardOpen(true); }}
                      className="opacity-0 group-hover/msg:opacity-100 transition-opacity p-1.5 rounded-full text-slate-400 hover:text-[#D9232E] hover:bg-red-50"
                      data-testid={`forward-message-${m.id}`}
                      title="Forward this message"
                      aria-label="Forward this message"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              );})}
            </div>

            <div className="border-t border-slate-200 bg-white p-3 md:p-4">
              <AttachmentPreview items={attachments} onRemove={(id) => setAttachments((a) => a.filter((x) => x.id !== id))} testIdPrefix="inbox-attach" />
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3 pb-2 border-b border-slate-100">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Add:</span>
                  <AttachToolbar
                    onEmoji={insertEmoji}
                    onAttach={(m) => setAttachments((a) => [...a, m])}
                    testIdPrefix="inbox-attach"
                  />
                  <TemplatePicker onSelect={(b) => setBody(b)} testId="inbox-template-picker" />
                  <label className="flex items-center gap-1.5 pl-2 border-l border-slate-200 cursor-pointer text-xs text-slate-600 select-none" title="Shortens long links (via TinyURL) so texts use fewer characters">
                    <input type="checkbox" checked={shortenLinks} onChange={(e) => setShortenLinks(e.target.checked)} className="accent-[#D9232E]" data-testid="inbox-shorten-toggle" />
                    Shorten links
                  </label>
                </div>
                <SegmentMeter body={body} onFix={setBody} testId="inbox-segment-meter" />
              </div>
              <div className="flex items-end gap-2">
                <Textarea
                  ref={textareaRef}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type a message... Enter adds a new line. Click Send to send."
                  className="flex-1 resize-none min-h-[64px] max-h-40 border-slate-200"
                  rows={2}
                  data-testid="message-input"
                />
                <Button
                  onClick={send}
                  disabled={sending || (!body.trim() && attachments.length === 0)}
                  className="h-11 px-5 bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold"
                  data-testid="send-message-button"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send</>}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      <ComposeModal
        open={forwardOpen}
        onOpenChange={setForwardOpen}
        onSent={() => { setForwardOpen(false); loadConvs(); }}
        initialBody={forwardBody}
        initialMediaUrls={forwardMedia}
      />
    </div>
  );
}
